import { useState } from 'react'
import { displayPosition, playerInitials, positionFill } from './board/positions'

// A player headshot, in a fixed box, with a designed monogram behind it.
//
// The box is the point: it is the same size whether the image loads or not, so
// nothing on any page reflows when a portrait fails, arrives late, or never
// arrives at all. Every call site renders it at one of the sizes below.
//
// `src` is the Sleeper CDN URL stored on the player row (SleeperSync derives
// it; nothing here constructs it). Three ways it ends up a monogram instead:
// manual-* players have no CDN image so the column is null; the CDN answers
// 403 for real ids it has no photo of, which fires onError; and on a LAN with
// no internet (README, "Draft night on a LAN") every image fails at once. All
// three land on the same designed fallback, which is why hotlinking is safe.
//
// It is aria-hidden everywhere: the player's name is always adjacent in text,
// so a portrait is decoration to a screen reader, not information.

export type PortraitSize = 'xs' | 'sm' | 'md' | 'lg' | 'tile' | 'board' | 'flash'

// Whole class names, never interpolated — Tailwind scans source text for
// candidates and would never see `w-[${x}px]`. Same rule as POSITION_FILL.
const PLATE: Record<PortraitSize, string> = {
  xs: 'h-[22px] w-[22px] rounded-[4px]',
  sm: 'h-[26px] w-[26px] rounded-[5px]',
  md: 'h-[34px] w-[34px] rounded-[7px]',
  lg: 'h-11 w-11 rounded-[9px]',
  // The board grid cell, which is the one place width is the scarce axis: the
  // cell is fixed at two lines in ~267x113 CSS px, and every pixel this takes
  // comes off a name already abbreviated for a couch. So it is the only
  // non-square plate — 3:4 off the cell's own height, which costs ~28% of the
  // name rather than the ~37% a square would. Sleeper's thumbs are 350x254
  // cutouts, so a 3:4 window keeps the middle 54%: the player, tight.
  tile: 'h-full w-auto aspect-[3/4] rounded-[0.4vh]',
  // The board sizes in vh like everything else on it, so a 4K panel and a
  // 1080p panel of the same diagonal render the same physical portrait.
  board: 'h-[9.5vh] w-[9.5vh] rounded-[1.2vh]',
  flash: 'h-[30vh] w-[30vh] rounded-[2vh]',
}

// The jersey badge, and the sizes that get one. It is empty everywhere except
// the two board sizes on purpose: the panel plates are 22-44px, and a number
// rendered inside one of those is a smudge. PlayerPool and RosterPanel print
// the jersey as real text in their own rows instead.
//
// `board` is empty because it has no call sites, not because a 9.5vh plate is
// too small for a number — it would need a step here the day it grows one.
//
// Sized in vh like everything else on the board (see PLATE), so the flash
// badge scales with its 30vh plate instead of staying tile-sized. Both steps
// reuse an existing --text-board-* token; a jersey does not warrant a new one.
const JERSEY: Record<PortraitSize, string> = {
  xs: '',
  sm: '',
  md: '',
  lg: '',
  tile: 'text-board-meta px-[0.4vh] py-[0.05vh]',
  board: '',
  flash: 'text-board-flash-meta px-[1vh] py-[0.25vh]',
}

const MONOGRAM: Record<PortraitSize, string> = {
  xs: 'text-[9px]',
  sm: 'text-[10.5px]',
  md: 'text-[13px]',
  lg: 'text-[17px]',
  tile: 'text-board-meta',
  board: 'text-board-team',
  flash: 'text-board-hero',
}

interface Props {
  fullName: string
  position?: string | null
  nflTeam?: string | null
  /** `players.portrait_url`. Null, or a URL that may 403 — both fall back. */
  src?: string | null
  /**
   * `players.jersey_number`. Null for a DST and for anyone Sleeper has no
   * number for; renders as no badge, never as a placeholder. Only the `tile`
   * and `flash` sizes draw it — see JERSEY.
   */
  jerseyNumber?: number | null
  size?: PortraitSize
  className?: string
}

export default function Portrait({
  fullName,
  position,
  nflTeam,
  src = null,
  jerseyNumber = null,
  size = 'sm',
  className = '',
}: Props) {
  // A broken URL must fall back to the monogram rather than leave a hole, so
  // the empty state stays the designed one even when an image was expected.
  const [failed, setFailed] = useState(false)
  const showImage = !!src && !failed
  const jersey = jerseyNumber != null ? JERSEY[size] : ''

  return (
    <span
      aria-hidden="true"
      className={`relative shrink-0 overflow-hidden bg-surface-3 shadow-raised ${PLATE[size]} ${className}`}
    >
      {/* A whisper of the position colour under the plate. It gives a column of
          portraits some variation without becoming a second position chip, and
          it is what a headshot's scrim will sit on later. */}
      <span
        className={`${positionFill(position)} absolute inset-0 opacity-[0.14]`}
      />

      {showImage ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      ) : (
        <span
          className={`absolute inset-0 flex items-center justify-center font-display font-bold tracking-wide text-ink-faint ${MONOGRAM[size]}`}
        >
          {playerInitials(fullName, position, nflTeam)}
        </span>
      )}

      {/* On the photo rather than on the meta line, which already ends in
          "#overall": a second #-prefixed number four characters away is
          unreadable across a room, where a number *on the headshot* is a
          jersey the way it is on an actual jersey. Position does the work
          punctuation cannot. Clipping to the plate's rounded corner is the
          intended look, so no negative offset.

          It inherits the plate's aria-hidden, and unlike the portrait itself
          that is *not* covered by "the name is always adjacent in text" — no
          board surface writes the number out. Decorative here on purpose: the
          board is a TV with no assistive-tech user, and the two panel surfaces
          (PlayerPool, RosterPanel) render the jersey as real text instead.

          Its own scrim, because it sits over an arbitrary headshot and the
          plate's position tint is far too faint to carry a glyph. pos-ink is
          the neutral already in play on both these surfaces; every accent is
          spoken for (signal/turn/warn/crit) and none of them means "jersey". */}
      {jersey && (
        <span
          className={`font-display absolute bottom-0 left-0 bg-pos-ink/75 leading-none font-bold text-ink-hi tabular-nums ${jersey}`}
        >
          {jerseyNumber}
        </span>
      )}
    </span>
  )
}

/**
 * The same plate with no player in it — an unfilled roster slot.
 *
 * It has to be the identical box, because a roster column that changes width or
 * row height as it fills would make the panel jump on every pick. Shows the
 * slot's own label ("FLEX", "BENCH") rather than initials.
 */
export function EmptyPortrait({
  label,
  size = 'md',
  className = '',
}: {
  label: string
  size?: PortraitSize
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={`relative flex shrink-0 items-center justify-center border border-dashed border-line-strong bg-ink/40 ${PLATE[size]} ${className}`}
    >
      <span
        className={`font-display font-bold tracking-wide text-ink-faint/70 ${MONOGRAM[size]}`}
      >
        {displayPosition(label)}
      </span>
    </span>
  )
}
